import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import NotificationToast from './NotificationToast';
import {
  ALCOHOL_PERCENT_KEYS,
  V2_DISPLAY_PERCENT_KEYS,
  getAlcoholResults,
  getAlcoholResultsForDisplay,
  getV2AlcoholResult,
  getV2AlcoholDecision,
  getV2FatValue,
  isV2AlcoholReading,
  formatV2AlcoholDecisionLabel,
  getAlcoholDecisionStatusClass,
  hasAlcoholUiResult,
  readingMatchesAllAlcoholUiFilters
} from '../utils/alcoholDisplay';
import {
  canShowFat,
  canShowScc,
  canShowAlcohol68,
  canShowAlcohol70,
  canShowAlcohol72,
  canShowAlcoholPercent,
  resolveDisplaySettings
} from '../utils/displaySettings';
import {
  formatFspUiValue,
  fspFilterPlaceholder,
  isValidFspFilterInput,
  matchesUiFspValue,
  parseFspNumber
} from '../utils/fspDisplay';
import './ReadingsList.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const FSP_API_URL = `${BASE_URL}/api/readings/fsp`;

const formatAlcoholScore = (value) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toFixed(3);
};

const ReadingsList = () => {
  const { user, refreshUser } = useAuth();
  const displaySettings = resolveDisplaySettings(user);
  const showFat = canShowFat(displaySettings);
  const showScc = canShowScc(displaySettings);
  const showAlcohol68 = canShowAlcohol68(displaySettings);
  const showAlcohol70 = canShowAlcohol70(displaySettings);
  const showAlcohol72 = canShowAlcohol72(displaySettings);
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingReading, setEditingReading] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  // Removed groupedReadings and collapsedLevels - using flat table now
  const [expandedReadings, setExpandedReadings] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });
  
  // Filter states - separate input values and applied filters
  const [machineIdInput, setMachineIdInput] = useState('');
  const [appliedMachineIdFilter, setAppliedMachineIdFilter] = useState('');
  
  const [farmCodeInput, setFarmCodeInput] = useState('');
  const [appliedFarmCodeFilter, setAppliedFarmCodeFilter] = useState('');
  
  const [unionNameInput, setUnionNameInput] = useState('');
  const [appliedUnionNameFilter, setAppliedUnionNameFilter] = useState('');
  
  const [dateInput, setDateInput] = useState('');
  const [appliedDateFilter, setAppliedDateFilter] = useState('');
  
  const [sccFilter, setSccFilter] = useState('all');
  const [alcohol68Filter, setAlcohol68Filter] = useState('all');
  const [alcohol70Filter, setAlcohol70Filter] = useState('all');
  const [alcohol72Filter, setAlcohol72Filter] = useState('all');
  
  const [fatValueInput, setFatValueInput] = useState('');
  const [appliedFatFilter, setAppliedFatFilter] = useState('');
  
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [notification, setNotification] = useState('');
  const notificationTimeoutRef = useRef(null);
  const [fspResultsByReading, setFspResultsByReading] = useState({});
  const [fspLoadingByReading, setFspLoadingByReading] = useState({});

  useEffect(() => {
    if (refreshUser) refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchReadings(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedMachineIdFilter, appliedFarmCodeFilter, appliedUnionNameFilter, appliedDateFilter, sccFilter, appliedFatFilter, alcohol68Filter, alcohol70Filter, alcohol72Filter]);

  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const readingsNeedingFsp = readings.filter((reading) => {
      const readingId = reading._id || reading.id;
      return (
        readingId &&
        !isV2AlcoholReading(reading) &&
        Array.isArray(reading.spectrum) &&
        reading.spectrum.length > 0 &&
        !reading.fspDisplay &&
        !fspResultsByReading[readingId] &&
        !fspLoadingByReading[readingId]
      );
    });

    readingsNeedingFsp.forEach((reading) => {
      fetchFspResult(reading);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readings]);

  // Removed groupReadingsByHierarchy effect as we're using flat table now

  const fetchReadings = async (page = 1) => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      // Add Machine ID filter if provided
      if (appliedMachineIdFilter && appliedMachineIdFilter.trim() !== '') {
        params.append('machineId', appliedMachineIdFilter.trim());
      }
      
      // Add Farm Code filter if provided
      if (appliedFarmCodeFilter && appliedFarmCodeFilter.trim() !== '') {
        params.append('farmCode', appliedFarmCodeFilter.trim());
      }
      
      // Add Union Name filter if provided
      if (appliedUnionNameFilter && appliedUnionNameFilter.trim() !== '') {
        params.append('unionName', appliedUnionNameFilter.trim());
      }
      
      // Add Date filter if provided
      if (appliedDateFilter && appliedDateFilter.trim() !== '') {
        params.append('date', appliedDateFilter.trim());
      }
      
      if (showScc && sccFilter && sccFilter !== 'all') {
        params.append('sccStatus', sccFilter);
      }
      
      if (showFat && appliedFatFilter) {
        params.append('fspFatFilter', appliedFatFilter);
      }

      if (showAlcohol68 && alcohol68Filter && alcohol68Filter !== 'all') {
        params.append('alcohol68Filter', alcohol68Filter);
      }
      if (showAlcohol70 && alcohol70Filter && alcohol70Filter !== 'all') {
        params.append('alcohol70Filter', alcohol70Filter);
      }
      if (showAlcohol72 && alcohol72Filter && alcohol72Filter !== 'all') {
        params.append('alcohol72Filter', alcohol72Filter);
      }
      
      const apiUrl = `${BASE_URL}/api/readings?${params.toString()}`;
      const fspFilterActive = !!(showFat && appliedFatFilter);
      console.log('Fetching readings with filters:', apiUrl);

      const response = await axios.get(apiUrl, {
        timeout: fspFilterActive ? 120000 : 60000
      });
      
      // Handle both old format (array) and new format (object with readings and pagination)
      if (Array.isArray(response.data)) {
        // Old format - backward compatibility
        setReadings(response.data);
      } else {
        const list = response.data.readings || [];
        setReadings(list);
        setPagination(response.data.pagination || pagination);

        const fspFromServer = {};
        list.forEach((reading) => {
          const readingId = reading._id || reading.id;
          if (readingId && reading.fspDisplay && !isV2AlcoholReading(reading)) {
            fspFromServer[readingId] = {
              fat: reading.fspDisplay.fat ?? null,
              protein: reading.fspDisplay.protein ?? null,
              snf: reading.fspDisplay.snf ?? null,
              adulterated: reading.fspDisplay.adulterated ?? null,
              adulterationLabel: reading.fspDisplay.adulterationLabel ?? null,
              adulterationProbabilities: reading.fspDisplay.adulterationProbabilities ?? null
            };
          }
        });
        if (Object.keys(fspFromServer).length > 0) {
          setFspResultsByReading((prev) => ({ ...prev, ...fspFromServer }));
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching readings:', error);
      console.error('Error details:', error.response?.data); // More detailed error log
      setLoading(false);
    }
  };

  const fetchFspResult = async (reading) => {
    const readingId = reading._id || reading.id;
    if (!readingId || isV2AlcoholReading(reading) || !Array.isArray(reading.spectrum) || reading.spectrum.length === 0) {
      return;
    }

    setFspLoadingByReading((prev) => ({ ...prev, [readingId]: true }));
    try {
      const response = await axios.post(
        FSP_API_URL,
        { spectra: reading.spectrum, readingId },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const result = response?.data?.results?.[0];
      if (result) {
        setFspResultsByReading((prev) => ({
          ...prev,
          [readingId]: {
            fat: parseFspNumber(result.fat),
            protein: parseFspNumber(result.protein),
            snf: parseFspNumber(result.snf),
            adulterated: typeof result.adulterated === 'boolean' ? result.adulterated : null,
            adulterationLabel: result?.adulteration?.label || null,
            adulterationProbabilities: result?.adulteration?.probabilities || null
          }
        }));
      }
    } catch (error) {
      console.error(`Error fetching FSP result for reading ${readingId}:`, error);
    } finally {
      setFspLoadingByReading((prev) => ({ ...prev, [readingId]: false }));
    }
  };

  const handleMachineIdInputChange = (e) => {
    setMachineIdInput(e.target.value);
  };

  const handleApplyMachineIdFilter = () => {
    const trimmed = machineIdInput.trim();
    setMachineIdInput(trimmed);
    setAppliedMachineIdFilter(trimmed);
    setCurrentPage(1);
  };

  const handleClearMachineIdFilter = () => {
    setMachineIdInput('');
    setAppliedMachineIdFilter('');
    setCurrentPage(1);
  };

  const handleFarmCodeInputChange = (e) => {
    setFarmCodeInput(e.target.value);
  };

  const handleApplyFarmCodeFilter = () => {
    const trimmed = farmCodeInput.trim();
    setFarmCodeInput(trimmed);
    setAppliedFarmCodeFilter(trimmed);
    setCurrentPage(1);
  };

  const handleClearFarmCodeFilter = () => {
    setFarmCodeInput('');
    setAppliedFarmCodeFilter('');
    setCurrentPage(1);
  };

  const handleUnionNameInputChange = (e) => {
    setUnionNameInput(e.target.value);
  };

  const handleApplyUnionNameFilter = () => {
    const trimmed = unionNameInput.trim();
    setUnionNameInput(trimmed);
    setAppliedUnionNameFilter(trimmed);
    setCurrentPage(1);
  };

  const handleClearUnionNameFilter = () => {
    setUnionNameInput('');
    setAppliedUnionNameFilter('');
    setCurrentPage(1);
  };

  const handleDateInputChange = (e) => {
    setDateInput(e.target.value);
  };

  const handleApplyDateFilter = () => {
    setAppliedDateFilter(dateInput);
    setCurrentPage(1);
  };

  const handleClearDateFilter = () => {
    setDateInput('');
    setAppliedDateFilter('');
    setCurrentPage(1);
  };

  const handleSccFilterChange = (e) => {
    setSccFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleAlcohol68FilterChange = (e) => {
    setAlcohol68Filter(e.target.value);
    setCurrentPage(1);
  };

  const handleAlcohol70FilterChange = (e) => {
    setAlcohol70Filter(e.target.value);
    setCurrentPage(1);
  };

  const handleAlcohol72FilterChange = (e) => {
    setAlcohol72Filter(e.target.value);
    setCurrentPage(1);
  };

  const handleFatValueInputChange = (e) => {
    setFatValueInput(e.target.value);
  };

  const handleApplyFatFilter = () => {
    const trimmed = fatValueInput.trim();
    if (isValidFspFilterInput(trimmed)) {
      setAppliedFatFilter(trimmed);
      setCurrentPage(1);
    } else {
      alert('Enter fat like 4.1 (4.10–4.19) as shown in the UI');
    }
  };

  const handleClearFatFilter = () => {
    setFatValueInput('');
    setAppliedFatFilter('');
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setMachineIdInput('');
    setAppliedMachineIdFilter('');
    setFarmCodeInput('');
    setAppliedFarmCodeFilter('');
    setUnionNameInput('');
    setAppliedUnionNameFilter('');
    setDateInput('');
    setAppliedDateFilter('');
    setSccFilter('all');
    setAlcohol68Filter('all');
    setAlcohol70Filter('all');
    setAlcohol72Filter('all');
    setFatValueInput('');
    setAppliedFatFilter('');
    setCurrentPage(1);
  };

  const hasAnyActiveFilter = () => {
    return appliedMachineIdFilter || appliedFarmCodeFilter || appliedUnionNameFilter || appliedDateFilter || (showScc && sccFilter && sccFilter !== 'all') || (showFat && appliedFatFilter) || (showAlcohol68 && alcohol68Filter && alcohol68Filter !== 'all') || (showAlcohol70 && alcohol70Filter && alcohol70Filter !== 'all') || (showAlcohol72 && alcohol72Filter && alcohol72Filter !== 'all');
  };

  const hasFspUiFilter = !!(showFat && appliedFatFilter);

  const readingMatchesFspFilters = (reading) => {
    if (!hasFspUiFilter) return true;

    const readingId = reading._id || reading.id;
    if (!Array.isArray(reading.spectrum) || reading.spectrum.length === 0) return false;

    const fspResult = fspResultsByReading[readingId];
    if (!fspResult) return false;

    if (showFat && appliedFatFilter && !matchesUiFspValue(fspResult.fat, appliedFatFilter)) return false;
    return true;
  };

  const hasActiveAlcoholUiFilter =
    (showAlcohol68 && alcohol68Filter && alcohol68Filter !== 'all') ||
    (showAlcohol70 && alcohol70Filter && alcohol70Filter !== 'all') ||
    (showAlcohol72 && alcohol72Filter && alcohol72Filter !== 'all');

  const readingMatchesDisplayFilters = (reading) => {
    if (!hasFspUiFilter && !readingMatchesFspFilters(reading)) return false;
    if (!hasActiveAlcoholUiFilter) return true;
    return readingMatchesAllAlcoholUiFilters(reading, {
      alcohol68: showAlcohol68 ? alcohol68Filter : 'all',
      alcohol70: showAlcohol70 ? alcohol70Filter : 'all',
      alcohol72: showAlcohol72 ? alcohol72Filter : 'all'
    });
  };

  // FSP filters are paginated on the server (10 matching rows per page)
  const displayReadings = hasFspUiFilter
    ? readings
    : readings.filter(readingMatchesDisplayFilters);

  // Removed groupReadingsByHierarchy - using flat table now

  const handleEdit = (reading) => {
    // Only allow editing regular readings (not spectrum readings)
    if (reading.spectrum) {
      alert('Spectrum readings cannot be edited');
      return;
    }
    setEditingReading(reading);
    setShowEditModal(true);
  };

  const handleDelete = async (readingId) => {
    if (!window.confirm('Are you sure you want to delete this reading?')) return;

    try {
      await axios.delete(`${BASE_URL}/api/readings/${readingId}`);
      showSuccessNotification('Reading deleted successfully');
      // Refresh current page, or go to previous page if current page becomes empty
      const remainingCount = pagination.totalCount - 1;
      const newTotalPages = Math.ceil(remainingCount / pagination.limit);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      } else {
        fetchReadings(currentPage);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting reading');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingReading(null);
    fetchReadings(currentPage);
  };

  const showSuccessNotification = (message) => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setNotification(message);
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification('');
    }, 5000);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Removed toggleCollapse and isCollapsed - using flat table now

  const toggleReadingExpand = (readingId) => {
    setExpandedReadings(prev => ({
      ...prev,
      [readingId]: !prev[readingId]
    }));
  };

  const isReadingExpanded = (readingId) => {
    return expandedReadings[readingId] === true;
  };

  // Union name may be stored as "Name||CollectionCenterCode" — show only name in Union column; code in Collection Center Code
  const parseUnionName = (unionNameString) => {
    if (!unionNameString || typeof unionNameString !== 'string') {
      return { unionNameDisplay: null, collectionCenterCode: null };
    }
    const trimmed = unionNameString.trim();
    const sep = '||';
    const idx = trimmed.indexOf(sep);
    if (idx === -1) {
      return { unionNameDisplay: trimmed || null, collectionCenterCode: null };
    }
    const namePart = trimmed.slice(0, idx).trim();
    const codePart = trimmed.slice(idx + sep.length).trim();
    return {
      unionNameDisplay: namePart || null,
      collectionCenterCode: codePart || null
    };
  };

  // Sample ID may be stored as "SAMPLE_71084|| adulterated"
  // If second part is adulterated, force adulterated status in UI.
  const isSampleIdAdulterated = (sampleId) => {
    if (!sampleId || typeof sampleId !== 'string') return false;
    const parts = sampleId.split('||').map((part) => part.trim().toLowerCase());
    return parts.length > 1 && parts[1] === 'adulterated';
  };

  const getFspAdulterationMeta = (fspResult) => {
    if (!fspResult) return null;
    const isAdulterated = fspResult.adulterated === true || fspResult.adulterationLabel === 'adulterated';
    const probabilities = fspResult.adulterationProbabilities || null;
    const probabilityValue = probabilities
      ? (isAdulterated ? probabilities.adulterated : probabilities.non_adulterated)
      : null;

    return {
      isAdulterated,
      label: isAdulterated ? 'Adulterated' : 'Non-Adulterated',
      probabilityValue: typeof probabilityValue === 'number' ? probabilityValue : null
    };
  };

  // Helper function to parse location into union code and location
  const parseLocation = (locationString) => {
    if (!locationString) return { unionCode: null, location: null };
    
    // Check if location contains "--" separator
    if (locationString.includes('--')) {
      const parts = locationString.split('--').map(part => part.trim());
      return {
        unionCode: parts[0] || null,
        location: parts[1] || null
      };
    }
    
    // If no separator, return the whole string as location
    return {
      unionCode: null,
      location: locationString
    };
  };

  // Helper function to get test results summary
  const getTestResultsSummary = (reading, fspResult) => {
    const isV2 = isV2AlcoholReading(reading);
    if (!isV2 && isSampleIdAdulterated(reading.sampleId)) {
      return { type: 'danger', text: 'Adulterated', full: 'Adulterated' };
    }
    const fspAdulteration = isV2 ? null : getFspAdulterationMeta(fspResult);
    if (fspAdulteration && fspAdulteration.isAdulterated) {
      return { type: 'danger', text: 'Adulterated', full: 'Adulterated' };
    }
    const v2Decision = getV2AlcoholDecision(reading.analysisResults);
    const results = getAlcoholResultsForDisplay(reading.analysisResults);
    const summary = [];
    
    if (!isV2) {
      if (fspAdulteration && !fspAdulteration.isAdulterated) {
        summary.push('Non-Adulterated');
      } else if (results && results.adulteration) {
        if (results.adulteration.label === 'adulterated') {
          return { type: 'danger', text: 'Adulterated', full: 'Adulterated' };
        } else {
          summary.push('Non-Adulterated');
        }
      }
    }
    
    if (v2Decision) {
      V2_DISPLAY_PERCENT_KEYS.forEach((pct) => {
        if (canShowAlcoholPercent(displaySettings, pct)) {
          summary.push(`Alcohol ${pct}%: ${formatV2AlcoholDecisionLabel(v2Decision)}`);
        }
      });
    } else if (results) {
      ALCOHOL_PERCENT_KEYS.forEach((pct) => {
        if (!canShowAlcoholPercent(displaySettings, pct)) return;
        const key = `alcohol_${pct}`;
        if (results[key] && results[key].label) {
          summary.push(`${pct}%: ${results[key].label.toUpperCase()}`);
        }
      });
    }

    const v2Fat = isV2 ? getV2FatValue(reading.analysisResults) : null;
    if (showFat && v2Fat != null) {
      summary.push(`Fat: ${formatFspUiValue(v2Fat)}`);
    } else if (showFat && fspResult && fspResult.fat != null) {
      summary.push(`Fat: ${formatFspUiValue(fspResult.fat)}`);
    }
    if (!isV2 && fspResult && fspResult.protein != null) {
      summary.push(`Protein: ${formatFspUiValue(fspResult.protein)}`);
    }
    if (!isV2 && fspResult && fspResult.snf != null) {
      summary.push(`SNF: ${formatFspUiValue(fspResult.snf)}`);
    }
    
    if (showScc && reading.analysisResults?.scc && reading.analysisResults.scc.pred_label && reading.analysisResults.scc.pred_label.length > 0) {
      const sccLabel = reading.analysisResults.scc.pred_label[0];
      // Handle new format: <300-low, >=300-high
      // Also handle old format for backward compatibility: <300, 300-1000, >1000
      let sccDisplay = '';
      if (sccLabel === '<300-low' || sccLabel === '<300') {
        sccDisplay = 'Low';
      } else if (sccLabel === '>=300-high' || sccLabel === '300-1000' || sccLabel === '>1000') {
        sccDisplay = 'High';
      } else {
        sccDisplay = sccLabel;
      }
      summary.push(sccDisplay);
    }
    
    return summary.length > 0
      ? {
          type: v2Decision === 'fail' ? 'danger' : 'success',
          text: summary.join(', '),
          full: summary
        }
      : null;
  };

  // Component to render a single reading row
  const renderReadingRow = (reading) => {
    const readingId = reading._id || reading.id;
    const isExpanded = isReadingExpanded(readingId);
    const isSpectrumReading = (reading.spectrum && reading.spectrum.length > 0) || reading.machineId;
    const title = isSpectrumReading ? (reading.farmCode || reading.scanName || reading.consignmentId || 'Spectrum Reading') : reading.farmCode;
    const fspResult = fspResultsByReading[readingId];
    const isFspLoading = !!fspLoadingByReading[readingId];
    const testSummary = getTestResultsSummary(reading, fspResult);
    const results = getAlcoholResultsForDisplay(reading.analysisResults);
    const isV2 = isV2AlcoholReading(reading);
    const fspAdulteration = isV2 ? null : getFspAdulterationMeta(fspResult);
    const hasAdulteration =
      !isV2 && (
        isSampleIdAdulterated(reading.sampleId) ||
        fspAdulteration?.isAdulterated ||
        results?.adulteration?.label === 'adulterated'
      );
    
    return (
      <>
        <tr className={`reading-row ${hasAdulteration ? 'adulterated-row' : ''}`}>
          <td className="reading-farmer-cell" data-label="Farm Code">
            <div className="farmer-content">
              <strong>{reading.farmCode || title || '-'}</strong>
            </div>
          </td>
          <td className="reading-farm-cell" data-label="Union Name">
            <div className="farm-content">
              {(() => {
                const { unionNameDisplay } = parseUnionName(reading.unionName);
                return unionNameDisplay ? (
                  <span className="union-name">{unionNameDisplay}</span>
                ) : (
                  <span className="text-muted">-</span>
                );
              })()}
            </div>
          </td>
          <td className="reading-machine-cell" data-label="Machine ID">
            {reading.machineId ? (
              <span className="machine-id">🔧 {reading.machineId}</span>
            ) : (
              <span className="text-muted">-</span>
            )}
          </td>
          <td className="reading-tests-cell" data-label="Test Results">
            {testSummary ? (
              <div className={`test-summary ${testSummary.type === 'danger' ? 'test-danger' : 'test-success'}`}>
                {testSummary.text}
              </div>
            ) : isSpectrumReading ? (
              <span className="text-muted">Pending...</span>
            ) : (
              <span className="test-value">Value: {reading.testValue}</span>
            )}
          </td>
          <td className="reading-date-cell" data-label="Date">
            <div className="date-content">
              <div>{new Date(reading.createdAt).toLocaleDateString()}</div>
              <div className="time-text">{new Date(reading.createdAt).toLocaleTimeString()}</div>
            </div>
          </td>
          <td className="reading-actions-cell" data-label="Actions">
            <div className="table-actions">
              {isSpectrumReading && (
                <button 
                  className="btn-view-details"
                  onClick={() => toggleReadingExpand(readingId)}
                  title={isExpanded ? 'Hide Details' : 'View Details'}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              )}
              {!isSpectrumReading && (
                <button className="btn-edit-table" onClick={() => handleEdit(reading)} title="Edit">Edit</button>
              )}
              <button className="btn-delete-table" onClick={() => handleDelete(readingId)} title="Delete">Delete</button>
            </div>
          </td>
        </tr>
        
        {/* Expanded Details Row */}
        {isExpanded && isSpectrumReading && (
          <tr className="reading-details-row">
            <td colSpan="6" className="details-cell">
              <div className="expanded-details-content">
                {/* Test Results Details */}
                {(isV2AlcoholReading(reading) ||
                  isSampleIdAdulterated(reading.sampleId) ||
                  isFspLoading ||
                  !!fspResult ||
                  (reading.analysisResults && (hasAlcoholUiResult(reading.analysisResults) || reading.analysisResults.scc))) && (
                  <div className="test-results-detailed">
                    <h4 className="details-section-title">Test Results</h4>
                    <div className="test-results-grid-detailed">
                      {(() => {
                        const results = getAlcoholResultsForDisplay(reading.analysisResults);
                        const v2Alcohol = getV2AlcoholResult(reading.analysisResults);
                        const v2Decision = getV2AlcoholDecision(reading.analysisResults);
                        const isV2 = isV2AlcoholReading(reading);
                        const fspAdulteration = isV2 ? null : getFspAdulterationMeta(fspResult);
                        const isAdulterated = !isV2 && (isSampleIdAdulterated(reading.sampleId) || fspAdulteration?.isAdulterated || (results && results.adulteration && results.adulteration.label === 'adulterated'));
                        const hasVisibleV2Alcohol = !!(
                          v2Decision &&
                          V2_DISPLAY_PERCENT_KEYS.some((pct) => canShowAlcoholPercent(displaySettings, pct))
                        );
                        const hasAnyAlcoholResult = !!(
                          hasVisibleV2Alcohol ||
                          (results &&
                          ALCOHOL_PERCENT_KEYS.some((pct) =>
                            canShowAlcoholPercent(displaySettings, pct) &&
                            results[`alcohol_${pct}`] &&
                            results[`alcohol_${pct}`].label
                          ))
                        );
                        
                        return (isAdulterated || hasAnyAlcoholResult || (results && results.adulteration)) && (
                          <>
                            {isAdulterated ? (
                              <div className="test-result-badge status-adulterated">
                                <span>
                                  Adulterated
                                  {fspAdulteration && fspAdulteration.probabilityValue !== null
                                    ? ` (${(fspAdulteration.probabilityValue * 100).toFixed(2)}%)`
                                    : ''}
                                </span>
                              </div>
                            ) : (
                              <>
                                {(fspAdulteration || (results && results.adulteration)) && (
                                  <div className="test-result-badge status-pass">Non-Adulterated</div>
                                )}
                                {v2Decision ? (
                                  <>
                                    {V2_DISPLAY_PERCENT_KEYS.map((pct) => (
                                      canShowAlcoholPercent(displaySettings, pct) ? (
                                        <div
                                          key={pct}
                                          className={`test-result-badge ${getAlcoholDecisionStatusClass(v2Decision)}`}
                                        >
                                          Alcohol {pct}%: {formatV2AlcoholDecisionLabel(v2Decision)}
                                        </div>
                                      ) : null
                                    ))}
                                    {hasVisibleV2Alcohol && formatAlcoholScore(v2Alcohol?.raw_score ?? v2Alcohol?.adjusted_score) && (
                                      <div className="test-result-badge status-info">
                                        Score: {formatAlcoholScore(v2Alcohol?.raw_score ?? v2Alcohol?.adjusted_score)}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {results && results.alcohol_60 && results.alcohol_60.label && (
                                      <div className={`test-result-badge ${results.alcohol_60.label === 'pass' ? 'status-pass' : 'status-fail'}`}>
                                        Alcohol 60%: {results.alcohol_60.label.toUpperCase()}
                                      </div>
                                    )}
                                    {results && results.alcohol_65 && results.alcohol_65.label && (
                                      <div className={`test-result-badge ${results.alcohol_65.label === 'pass' ? 'status-pass' : 'status-fail'}`}>
                                        Alcohol 65%: {results.alcohol_65.label.toUpperCase()}
                                      </div>
                                    )}
                                    {showAlcohol68 && results && results.alcohol_68 && results.alcohol_68.label && (
                                      <div className={`test-result-badge ${results.alcohol_68.label === 'pass' ? 'status-pass' : 'status-fail'}`}>
                                        Alcohol 68%: {results.alcohol_68.label.toUpperCase()}
                                      </div>
                                    )}
                                    {showAlcohol70 && results && results.alcohol_70 && results.alcohol_70.label && (
                                      <div className={`test-result-badge ${results.alcohol_70.label === 'pass' ? 'status-pass' : 'status-fail'}`}>
                                        Alcohol 70%: {results.alcohol_70.label.toUpperCase()}
                                      </div>
                                    )}
                                    {showAlcohol72 && results && results.alcohol_72 && results.alcohol_72.label && (
                                      <div className={`test-result-badge ${results.alcohol_72.label === 'pass' ? 'status-pass' : 'status-fail'}`}>
                                        Alcohol 72%: {results.alcohol_72.label.toUpperCase()}
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </>
                        );
                      })()}
                      {(() => {
                        const results = getAlcoholResults(reading.analysisResults);
                        const isV2 = isV2AlcoholReading(reading);
                        const fspAdulteration = isV2 ? null : getFspAdulterationMeta(fspResult);
                        const isAdulterated = !isV2 && (isSampleIdAdulterated(reading.sampleId) || fspAdulteration?.isAdulterated || (results && results.adulteration && results.adulteration.label === 'adulterated'));
                        const v2Fat = isV2 ? getV2FatValue(reading.analysisResults) : null;
                        
                        // Only show fat and SCC if not adulterated
                        if (isAdulterated) {
                          return null;
                        }
                        
                        return (
                          <>
                            {!isV2 && isFspLoading && (
                              <div className="test-result-badge status-info">
                                FSP: Calculating...
                              </div>
                            )}
                            {showFat && v2Fat != null && (
                              <div className="test-result-badge status-info">
                                Fat: {formatFspUiValue(v2Fat)}
                              </div>
                            )}
                            {showFat && !isV2 && fspResult && fspResult.fat != null && (
                              <div className="test-result-badge status-info">
                                Fat: {formatFspUiValue(fspResult.fat)}
                              </div>
                            )}
                            {!isV2 && fspResult && fspResult.protein != null && (
                              <div className="test-result-badge status-info">
                                Protein: {formatFspUiValue(fspResult.protein)}
                              </div>
                            )}
                            {!isV2 && fspResult && fspResult.snf != null && (
                              <div className="test-result-badge status-info">
                                SNF: {formatFspUiValue(fspResult.snf)}
                              </div>
                            )}
                            {showScc && reading.analysisResults?.scc && reading.analysisResults.scc.pred_label && reading.analysisResults.scc.pred_label.length > 0 && (() => {
                              const sccLabel = reading.analysisResults.scc.pred_label[0];
                              // Handle new format: <300-low, >=300-high
                              // Also handle old format for backward compatibility: <300, 300-1000, >1000
                              let displayText = '';
                              let statusClass = 'status-info';
                              
                              // Normalize label - extract category from formats like "<300-low" or ">=300-high"
                              const normalizedLabel = sccLabel.toLowerCase();
                              
                              if (normalizedLabel.includes('<300') || normalizedLabel === '<300' || normalizedLabel === '<300-low') {
                                displayText = 'Low';
                                statusClass = 'status-low';
                              } else if (normalizedLabel.includes('>=300') || normalizedLabel.includes('300-1000') || normalizedLabel.includes('>1000') || normalizedLabel === '>=300-high' || normalizedLabel === '300-1000' || normalizedLabel === '>1000') {
                                displayText = 'High';
                                statusClass = 'status-high';
                              } else {
                                // Fallback: try to extract category from label
                                if (normalizedLabel.includes('low')) {
                                  displayText = 'Low';
                                  statusClass = 'status-low';
                                } else if (normalizedLabel.includes('high')) {
                                  displayText = 'High';
                                  statusClass = 'status-high';
                                } else {
                                  displayText = 'Unknown';
                                }
                              }
                              
                              return (
                                <div className={`test-result-badge ${statusClass}`}>
                                  SCC: {displayText}
                                </div>
                              );
                            })()}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
                
                {/* Additional Information */}
                <div className="additional-info-detailed">
                  <h4 className="details-section-title">Additional Information</h4>
                  <div className="info-grid-detailed">
                    {reading.machineId && (
                      <div className="info-item-detailed">
                        <span className="info-label-formal">Machine ID</span>
                        <span className="info-value-formal">{reading.machineId}</span>
                      </div>
                    )}
                    {reading.farmCode && (
                      <div className="info-item-detailed">
                        <span className="info-label-formal">Farm Code</span>
                        <span className="info-value-formal">{reading.farmCode}</span>
                      </div>
                    )}
                    {(() => {
                      const { unionNameDisplay, collectionCenterCode: unionCollectionCode } = parseUnionName(reading.unionName);
                      const { unionCode: locationUnionCode, location } = parseLocation(reading.location);
                      const collectionCenterCode = unionCollectionCode || locationUnionCode;

                      return (
                        <>
                          {unionNameDisplay && (
                            <div className="info-item-detailed">
                              <span className="info-label-formal">Union Name</span>
                              <span className="info-value-formal">{unionNameDisplay}</span>
                            </div>
                          )}
                          {collectionCenterCode && (
                            <div className="info-item-detailed">
                              <span className="info-label-formal">Collection Center Code</span>
                              <span className="info-value-formal">{collectionCenterCode}</span>
                            </div>
                          )}
                          {location && (
                            <div className="info-item-detailed">
                              <span className="info-label-formal">Location</span>
                              <span className="info-value-formal">{location}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {reading.employeeId && (
                      <div className="info-item-detailed">
                        <span className="info-label-formal">Employee</span>
                        <span className="info-value-formal">{reading.employeeId?.name || 'N/A'}</span>
                      </div>
                    )}
                    {reading.managerId && (
                      <div className="info-item-detailed">
                        <span className="info-label-formal">Manager</span>
                        <span className="info-value-formal">{reading.managerId?.name || 'N/A'}</span>
                      </div>
                    )}
                    {reading.clientId && (
                      <div className="info-item-detailed">
                        <span className="info-label-formal">Client</span>
                        <span className="info-value-formal">{reading.clientId?.name || 'N/A'}</span>
                      </div>
                    )}
                    <div className="info-item-detailed">
                      <span className="info-label-formal">Date</span>
                      <span className="info-value-formal date-formal">{new Date(reading.createdAt).toLocaleString('en-US', { 
                        year: 'numeric', 
                        month: 'numeric', 
                        day: 'numeric', 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        )}
      </>
    );
  };

  // Render flat table structure (no hierarchy)
  const renderHierarchicalReadings = () => {
    if (!user) return null;

    // Show flat table for all roles
    return (
      <div className="readings-table-container">
        <table className="readings-table">
          <thead>
            <tr>
              <th>Farm Code</th>
              <th>Union Name</th>
              <th>Machine ID</th>
              <th>Test Results</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayReadings.map(reading => renderReadingRow(reading))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) return <div className="readings-list"><div className="loading-state">Loading readings...</div></div>;

  return (
    <div className="readings-list">
      <NotificationToast message={notification} />
      <div className="readings-header-with-pagination">
        <div className="header-left">
          <h1>{user?.role === 'employee' ? 'My Consignments' : 'Consignments'}</h1>
        </div>
      </div>
      
      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-header-section">
          <h3 className="filters-section-title">Filters</h3>
          <div className="filters-header-actions">
            {hasAnyActiveFilter() && (
              <button 
                className="clear-all-filters-btn"
                onClick={handleClearAllFilters}
                title="Clear all filters"
              >
                Clear All
              </button>
            )}
            <button 
              className="filter-toggle-btn"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              title={filtersExpanded ? 'Collapse filters' : 'Expand filters'}
            >
              {filtersExpanded ? '▼' : '▶'}
            </button>
          </div>
        </div>
        
        <div className={`filters-grid ${filtersExpanded ? 'filters-expanded' : 'filters-collapsed'}`}>
          {/* Machine ID Filter */}
          <div className="filter-item">
            <label htmlFor="machineIdFilter" className="filter-label">
              Machine ID
            </label>
            <div className="filter-input-group">
              <input
                id="machineIdFilter"
                type="text"
                value={machineIdInput}
                onChange={handleMachineIdInputChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleApplyMachineIdFilter();
                  }
                }}
                placeholder="Enter Machine ID"
                className="filter-input-field"
              />
              {appliedMachineIdFilter ? (
                <button 
                  className="clear-filter-btn-icon"
                  onClick={handleClearMachineIdFilter}
                  title="Clear filter"
                >
                  ×
                </button>
              ) : (
                <button 
                  className="apply-filter-btn-icon"
                  onClick={handleApplyMachineIdFilter}
                  title="Apply filter"
                  disabled={!machineIdInput || machineIdInput.trim() === ''}
                >
                  ✓
                </button>
              )}
            </div>
          </div>

          {/* Farm Code Filter */}
          <div className="filter-item">
            <label htmlFor="farmCodeFilter" className="filter-label">
              Farm Code
            </label>
            <div className="filter-input-group">
              <input
                id="farmCodeFilter"
                type="text"
                value={farmCodeInput}
                onChange={handleFarmCodeInputChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleApplyFarmCodeFilter();
                  }
                }}
                placeholder="Enter Farm Code"
                className="filter-input-field"
              />
              {appliedFarmCodeFilter ? (
                <button 
                  className="clear-filter-btn-icon"
                  onClick={handleClearFarmCodeFilter}
                  title="Clear filter"
                >
                  ×
                </button>
              ) : (
                <button 
                  className="apply-filter-btn-icon"
                  onClick={handleApplyFarmCodeFilter}
                  title="Apply filter"
                  disabled={!farmCodeInput || farmCodeInput.trim() === ''}
                >
                  ✓
                </button>
              )}
            </div>
          </div>

          {/* Union Name Filter */}
          <div className="filter-item">
            <label htmlFor="unionNameFilter" className="filter-label">
              Union Name
            </label>
            <div className="filter-input-group">
              <input
                id="unionNameFilter"
                type="text"
                value={unionNameInput}
                onChange={handleUnionNameInputChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleApplyUnionNameFilter();
                  }
                }}
                placeholder="Enter Union Name"
                className="filter-input-field"
              />
              {appliedUnionNameFilter ? (
                <button 
                  className="clear-filter-btn-icon"
                  onClick={handleClearUnionNameFilter}
                  title="Clear filter"
                >
                  ×
                </button>
              ) : (
                <button 
                  className="apply-filter-btn-icon"
                  onClick={handleApplyUnionNameFilter}
                  title="Apply filter"
                  disabled={!unionNameInput || unionNameInput.trim() === ''}
                >
                  ✓
                </button>
              )}
            </div>
          </div>

          {/* Date Filter */}
          <div className="filter-item">
            <label htmlFor="dateFilter" className="filter-label">
              Date
            </label>
            <div className="filter-input-group">
              <input
                id="dateFilter"
                type="date"
                value={dateInput}
                onChange={handleDateInputChange}
                className="filter-input-field"
              />
              {appliedDateFilter ? (
                <button 
                  className="clear-filter-btn-icon"
                  onClick={handleClearDateFilter}
                  title="Clear filter"
                >
                  ×
                </button>
              ) : (
                <button 
                  className="apply-filter-btn-icon"
                  onClick={handleApplyDateFilter}
                  title="Apply filter"
                  disabled={!dateInput}
                >
                  ✓
                </button>
              )}
            </div>
          </div>

          {/* SCC Status Filter */}
          {showScc && (
          <div className="filter-item">
            <label htmlFor="sccFilter" className="filter-label">
              SCC Status
            </label>
            <div className="filter-input-group">
              <select
                id="sccFilter"
                value={sccFilter}
                onChange={handleSccFilterChange}
                className="filter-input-field"
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="all">All SCC Values</option>
                <option value="low">Low</option>
                <option value="high">High</option>
              </select>
              {sccFilter && sccFilter !== 'all' ? (
                <button 
                  className="clear-filter-btn-icon"
                  onClick={() => setSccFilter('all')}
                  title="Clear filter"
                >
                  ×
                </button>
              ) : (
                <div style={{ width: '24px', height: '24px' }}></div>
              )}
            </div>
          </div>
          )}

          {/* Fat Value Filter */}
          {showFat && (
          <div className="filter-item">
            <label htmlFor="fatValueFilter" className="filter-label">
              Fat Value
            </label>
            <div className="filter-input-group">
              <input
                id="fatValueFilter"
                type="number"
                step="0.01"
                value={fatValueInput}
                onChange={handleFatValueInputChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleApplyFatFilter();
                  }
                }}
                placeholder={fspFilterPlaceholder}
                className="filter-input-field"
              />
              {appliedFatFilter ? (
                <button 
                  className="clear-filter-btn-icon"
                  onClick={handleClearFatFilter}
                  title="Clear filter"
                >
                  ×
                </button>
              ) : (
                <button 
                  className="apply-filter-btn-icon"
                  onClick={handleApplyFatFilter}
                  title="Apply filter"
                  disabled={!isValidFspFilterInput(fatValueInput)}
                >
                  ✓
                </button>
              )}
            </div>
          </div>
          )}

          {/* Alcohol 68% Filter */}
          {showAlcohol68 && (
          <div className="filter-item alcohol-filter">
            <label htmlFor="alcohol68Filter" className="filter-label">
              Alc 68%
            </label>
            <div className="filter-input-group">
              <select
                id="alcohol68Filter"
                value={alcohol68Filter}
                onChange={handleAlcohol68FilterChange}
                className="filter-input-field"
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="all">All</option>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
                <option value="refer">Refer</option>
              </select>
              {alcohol68Filter && alcohol68Filter !== 'all' ? (
                <button 
                  className="clear-filter-btn-icon"
                  onClick={() => setAlcohol68Filter('all')}
                  title="Clear filter"
                >
                  ×
                </button>
              ) : (
                <div style={{ width: '24px', height: '24px' }}></div>
              )}
            </div>
          </div>
          )}

          {/* Alcohol 70% Filter */}
          {showAlcohol70 && (
          <div className="filter-item alcohol-filter">
            <label htmlFor="alcohol70Filter" className="filter-label">
              Alc 70%
            </label>
            <div className="filter-input-group">
              <select
                id="alcohol70Filter"
                value={alcohol70Filter}
                onChange={handleAlcohol70FilterChange}
                className="filter-input-field"
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="all">All</option>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
                <option value="refer">Refer</option>
              </select>
              {alcohol70Filter && alcohol70Filter !== 'all' ? (
                <button 
                  className="clear-filter-btn-icon"
                  onClick={() => setAlcohol70Filter('all')}
                  title="Clear filter"
                >
                  ×
                </button>
              ) : (
                <div style={{ width: '24px', height: '24px' }}></div>
              )}
            </div>
          </div>
          )}

          {/* Alcohol 72% Filter */}
          {showAlcohol72 && (
          <div className="filter-item alcohol-filter">
            <label htmlFor="alcohol72Filter" className="filter-label">
              Alc 72%
            </label>
            <div className="filter-input-group">
              <select
                id="alcohol72Filter"
                value={alcohol72Filter}
                onChange={handleAlcohol72FilterChange}
                className="filter-input-field"
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="all">All</option>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
                <option value="refer">Refer</option>
              </select>
              {alcohol72Filter && alcohol72Filter !== 'all' ? (
                <button 
                  className="clear-filter-btn-icon"
                  onClick={() => setAlcohol72Filter('all')}
                  title="Clear filter"
                >
                  ×
                </button>
              ) : (
                <div style={{ width: '24px', height: '24px' }}></div>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
      
      {readings.length === 0 ? (
        <div className="empty-state">
          <p>No readings found</p>
        </div>
      ) : displayReadings.length === 0 && (hasFspUiFilter || hasActiveAlcoholUiFilter) ? (
        <div className="empty-state">
          <p>
            {loading && hasFspUiFilter
              ? 'Loading filtered readings…'
              : hasFspUiFilter
                ? 'No readings match the Fat filter. Try 5 (5.00–5.99) or 5.1 (5.10–5.19).'
                : 'No readings match the alcohol filter on this page.'}
          </p>
        </div>
      ) : (
        <>
          {renderHierarchicalReadings()}
          
          {/* Pagination Controls */}
          {(pagination.totalPages > 1 || pagination.totalCount > 0) && (
            <div className="pagination-wrapper">
              {pagination.totalPages > 1 && (
                <div className="pagination-controls">
                  <button 
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!pagination.hasPrevPage || loading}
                  >
                    ← Previous
                  </button>
                  
                  <div className="pagination-numbers">
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNum === 1 ||
                        pageNum === pagination.totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return <span key={pageNum} className="pagination-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <button 
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.hasNextPage || loading}
                  >
                    Next →
                  </button>
                </div>
              )}
              
              {pagination.totalCount > 0 && (
                <div className="pagination-info">
                  Showing {((currentPage - 1) * pagination.limit) + 1} - {Math.min(currentPage * pagination.limit, pagination.totalCount)} of {pagination.totalCount} readings
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showEditModal && editingReading && (
        <EditReadingModal
          reading={editingReading}
          onClose={handleModalClose}
          onSuccess={showSuccessNotification}
        />
      )}
    </div>
  );
};

const EditReadingModal = ({ reading, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    farmCode: reading.farmCode,
    location: reading.location,
    testValue: reading.testValue
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.put(`${BASE_URL}/api/readings/${reading._id || reading.id}`, formData);
      if (onSuccess) {
        onSuccess('Reading updated successfully');
      }
      onClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Error updating reading');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Reading</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Farm Code *</label>
            <input
              type="text"
              value={formData.farmCode}
              onChange={(e) => setFormData({ ...formData, farmCode: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Location *</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Test Value *</label>
            <input
              type="number"
              step="0.01"
              value={formData.testValue}
              onChange={(e) => setFormData({ ...formData, testValue: parseFloat(e.target.value) })}
              required
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReadingsList;

